import { Button, Modal, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUser } from '@/services';

const fieldLabelProps = {
  style: { letterSpacing: '0.08em', fontSize: '0.6875rem', fontWeight: 700 },
};

interface CreateUserFormValues {
  displayName: string;
  email: string;
  password: string;
}

interface CreateUserModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Admin-only account creation — there is no self-registration flow, so
 * this is the only way a new driver gets a login.
 */
export const CreateUserModal = ({ opened, onClose }: CreateUserModalProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CreateUserFormValues>({
    initialValues: { displayName: '', email: '', password: '' },
    validate: {
      displayName: (value) =>
        value.trim().length > 0 ? null : t('modals.createUser.errors.nameRequired'),
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t('modals.createUser.errors.validEmail'),
      password: (value) => (value.length >= 6 ? null : t('modals.createUser.errors.minLength')),
    },
  });

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values: CreateUserFormValues) => {
    setSubmitting(true);
    try {
      await createUser(values.email, values.password, values.displayName.trim());
      notifications.show({
        color: 'signal',
        message: t('modals.createUser.notifications.created', { name: values.displayName }),
      });
      handleClose();
    } catch (error) {
      notifications.show({
        color: 'red',
        message: error instanceof Error ? error.message : t('modals.createUser.errors.generic'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      centered
      onClose={handleClose}
      opened={opened}
      title={
        <Text c="signal.7" ff="monospace" fw={700} size="xs" style={{ letterSpacing: '0.2em' }}>
          {t('modals.createUser.title')}
        </Text>
      }
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label={t('modals.createUser.name')}
            labelProps={fieldLabelProps}
            placeholder={t('modals.createUser.namePlaceholder')}
            {...form.getInputProps('displayName')}
          />
          <TextInput
            label={t('modals.createUser.email')}
            labelProps={fieldLabelProps}
            placeholder={t('modals.createUser.emailPlaceholder')}
            {...form.getInputProps('email')}
          />
          <PasswordInput
            label={t('modals.createUser.tempPassword')}
            labelProps={fieldLabelProps}
            placeholder={t('modals.createUser.tempPasswordPlaceholder')}
            {...form.getInputProps('password')}
          />
          <Button
            fullWidth
            loading={submitting}
            style={{ boxShadow: 'none', fontWeight: 700, letterSpacing: '0.06em' }}
            type="submit"
          >
            {t('modals.createUser.create')}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
};
